import { useState, useEffect } from "react";
import { Form, Button, Alert, Row, Col, Spinner } from "react-bootstrap";
import {
  getDocumentsListRequest,
  getDocumentTypesListRequest,
  uploadDocumentRequest,
  patchBusinessKycRequest,
} from "@/services";
import DocumentUploadForm from "../../components/document_upload_form";
import { uploadEachFile, describeUploadFailures } from "../../components/upload_utils";
import { useParams } from "react-router-dom";

const BusinessDocumentsForm = ({ business, goToNextStep, reloadBusinessKyc, goBack, documentsList, reloadDocumentsList, isKycReadonly }) => {
  const { applicationId: applicationUuid } = useParams();
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [files, setFiles] = useState({});

  // refetch documents & document types every time business_type changes
  useEffect(() => {
    // Which documents are required depends on the business type, so there is
    // nothing to load until Business Details has been saved.
    if (!business?.business_type) {
      setDocumentTypes([]);
      setLoadingOptions(false);
      return;
    }

    const fetchDocs = async () => {
      setLoadingOptions(true);
      try {
        const [documentTypesRes] = await Promise.all([
          getDocumentTypesListRequest({
            category: "business_kyc",
            business_type_id: business.business_type,
          }),
        ]);

        setDocumentTypes(documentTypesRes?.results || []);
      } catch (err) {
        setErrors({
          general: "Failed to load required documents. Please refresh.",
        });
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchDocs();
  }, [business?.business_type]);

  const onFileUpload = async (docType, event, afterEventCallback) => {
    const selectedFiles = Array.from(event.target.files);
    await uploadFile(docType, selectedFiles);
    if (afterEventCallback) afterEventCallback(docType);
  };

  const uploadFile = async (docType, selectedFiles) => {
    const doc = documentTypes.find(doc => doc.document_type === docType);
    setUploading(true);
    // Clear only this document's error so other missing-document hints stay visible
    // eslint-disable-next-line no-unused-vars
    setErrors(({ general, [doc?.document_type_key]: _uploaded, ...rest }) => rest);

    try {
      if (doc.is_mandatory && selectedFiles.length === 0) {
        throw new Error(`${doc.title} is mandatory`);
      }

      const failures = await uploadEachFile(selectedFiles, (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append("document_type_id", doc.document_type);
        formDataUpload.append("loan_id", applicationUuid);
        formDataUpload.append("business_type_id", business.business_type);
        formDataUpload.append("file", file);
        return uploadDocumentRequest(formDataUpload);
      });
      if (failures.length) {
        setErrors((prev) => ({ ...prev, general: describeUploadFailures(failures, selectedFiles.length) }));
      }
      await reloadDocumentsList();
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        general: err?.message || "Failed to Upload Document. Try again.",
      }));
    } finally {
      setUploading(false);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isKycReadonly) {
      if (goToNextStep) goToNextStep();
    } else {
      setErrors({});

      // Flag every missing mandatory document at once, next to its upload field
      const missingDocs = documentTypes.filter((doc) => {
        if (!doc.is_mandatory) return false;
        const uploadedFiles = documentsList
          .filter(document => document.document_type == doc.document_type)
          .flatMap(document => document.document_files || []);
        return uploadedFiles.length === 0;
      });
      if (missingDocs.length) {
        const newErrors = {
          general: `Please upload the missing documents: ${missingDocs.map((doc) => doc.title).join(", ")}.`,
        };
        missingDocs.forEach((doc) => {
          newErrors[doc.document_type_key] = `Please upload ${doc.title}`;
        });
        setErrors(newErrors);
        const firstSection = document.getElementById(`doc-section-${missingDocs[0].document_type_key}`);
        firstSection?.scrollIntoView({ behavior: "smooth", block: "center" });
        firstSection?.focus();
        return;
      }

      setSubmitting(true);
      try {
        const res = await patchBusinessKycRequest(business?.uuid, { status: 'completed', loan_id: applicationUuid });

        if (res?.success === false) {
          setErrors(res.error || { general: res.message || "Something went wrong" });
          return;
        }
        await reloadBusinessKyc();
        if (goToNextStep) goToNextStep();
      } catch (err) {
        setErrors({
          general: err?.message || "Failed to submit Business KYC. Try again.",
        });
      } finally {
        setSubmitting(false);
      }
    }

  };

  if (loadingOptions && !documentTypes.length) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Loading KYC options...</span>
      </div>
    );
  }

  if (!business?.business_type) {
    return (
      <div className="p-4">
        <Alert variant="info" className="mb-3">
          Please complete and save the Business Details tab first. The documents we need
          depend on your business type.
        </Alert>
        <Button id="b_back_business_kyc_documents_form" variant="secondary" onClick={goBack}>
          Back to Business Details
        </Button>
      </div>
    );
  }

  return (
    <Form onSubmit={handleSubmit} className="mb-4 w-full">
      {errors.general && <Alert variant="danger">{errors.general}</Alert>}
      <fieldset disabled={isKycReadonly}>
        <Row>
          {/* Documents */}
          <DocumentUploadForm
            documentTypes={documentTypes}
            documentsList={documentsList}
            showLoader={loadingOptions}
            onChange={onFileUpload}
            files={files}
            setFiles={setFiles}
            reloadDocumentsList={reloadDocumentsList}
            errors={errors}
            uploading={uploading}
            onUploadingChange={setUploading}
          />
        </Row>
      </fieldset>
      <div className="d-flex justify-content-end gap-2">
        <Button id="b_back_business_kyc_documents_form" variant="secondary" onClick={goBack} disabled={uploading}>
          Back
        </Button>
        <Button
          id="b_submit_business_kyc_documents_form"
          type="submit"
          variant="primary"
          disabled={submitting || uploading}
          data-testid="fincobox-kyc-business-submit"
        >
          {submitting ? "Saving..." : uploading ? "Uploading..." : isKycReadonly ? "Continue" : "Save & Continue"}
        </Button>
      </div>
    </Form>
  );
};

export default BusinessDocumentsForm;
